import {apiFetch} from '../../../../security/apiFetch';
import React, {useEffect, useState} from 'react';
import {Card, CardContent, Typography, Box, Button, Alert, Accordion, AccordionSummary, AccordionDetails} from '@mui/material';
import {ExpandMore, VolumeUp} from '@mui/icons-material';
import {LessonStep} from '../../types';
import {playAudio} from '../../../../utils/audioUtils';

interface TajweedStepProps {
  step: LessonStep;
  token: string | null;
  onNext: (score?: number) => void;
  onBackToLetters?: () => void;
  lessonId?: string;
}
interface TajweedRule {
  id: string;
  name: string;
  description?: string | null;
  example: string;
  source_span_text?: string;
  surah?: number;
  ayah?: number;
  audio_url?: string;
}
const TajweedStep: React.FC<TajweedStepProps> = ({step, token, onNext}) => {
  const [rules, setRules] = useState<TajweedRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [viewed, setViewed] = useState<Set<string>>(new Set());
  const [playing, setPlaying] = useState<string | null>(null);
  const [source, setSource] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setRules([]); setError(null); setAudioError(null); setLoading(true); setViewed(new Set()); setSource(''); setExpanded(false);
    const load = async () => {
      try {
        if (!step.content.letter) throw new Error('Для этого шага не указана буква.');
        const response = await apiFetch(`/api/v1/arabic-lessons/tajweed/${step.content.letter}`, {
          headers: token ? {Authorization: `Bearer ${token}`} : {}, signal: controller.signal
        });
        if (response.status === 404) throw new Error('В установленном источнике нет специальных аннотированных примеров для этой буквы.');
        if (!response.ok) throw new Error(response.status === 401 ? 'Сессия закончилась. Войдите снова.' : 'Источник примеров временно недоступен.');
        const data = await response.json();
        if (!Array.isArray(data.rules) || !data.rules.length || !data.rules.every((rule: any) => typeof rule.id === 'string' && typeof rule.name === 'string' && typeof rule.example === 'string')) {
          throw new Error('Источник вернул неполные данные примеров.');
        }
        if (!controller.signal.aborted) {
          setRules(data.rules);
          if (typeof data.source?.repository === 'string') setSource(data.source.repository);
        }
      } catch (failure) {
        if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : 'Не удалось загрузить исходные примеры.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [step.content.letter, token, retry]);
  const play = async (rule: TajweedRule) => {
    if (!rule.audio_url) return;
    setPlaying(rule.id); setAudioError(null);
    try {await playAudio(rule.audio_url, token);}
    catch {setAudioError('Не удалось воспроизвести запись.');}
    finally {setPlaying(null);}
  };
  const allViewed = rules.length > 0 && rules.every(rule => viewed.has(rule.id));
  return <Card sx={{mb: 3}}><CardContent>
    <Typography variant="h5" gutterBottom>{step.title}</Typography>
    {step.content.instruction && <Typography sx={{mb: 2}}>{step.content.instruction}</Typography>}
    <Typography variant="h6" gutterBottom>Исходные примеры для буквы «{step.content.letter}»</Typography>
    {loading && <Alert severity="info">Загружаем исходные примеры…</Alert>}
    {error && <Alert severity="error" action={<Button color="inherit" onClick={() => setRetry(value => value + 1)}>Повторить</Button>}>{error}</Alert>}
    {audioError && <Alert severity="error">{audioError}</Alert>}
    {source && <Typography variant="caption" component="p" sx={{mb: 2}}>Источник: {source}. Показаны реальные интервалы в конкретных аятах; они не определяют правило для всех вхождений буквы. Оценка произношения не выполняется.</Typography>}
    {rules.map(rule => <Accordion key={rule.id} expanded={expanded === rule.id} onChange={(_, open) => {
      setExpanded(open ? rule.id : false);
      if (open) setViewed(previous => new Set(previous).add(rule.id));
    }}>
      <AccordionSummary expandIcon={<ExpandMore />}><Typography>{rule.name}</Typography></AccordionSummary>
      <AccordionDetails>
        {rule.description && <Typography sx={{mb: 2}}>{rule.description}</Typography>}
        {rule.surah !== undefined && rule.ayah !== undefined && <Typography variant="caption">Сура {rule.surah}, аят {rule.ayah}</Typography>}
        <Typography dir="rtl" sx={{fontFamily: 'Noto Naskh Arabic, serif', fontSize: 24, my: 1}}>{rule.example}</Typography>
        {rule.source_span_text && <Typography dir="rtl">Размеченный фрагмент: {rule.source_span_text}</Typography>}
        {rule.audio_url && <Button startIcon={<VolumeUp />} disabled={playing === rule.id} onClick={() => play(rule)}>{playing === rule.id ? 'Играет…' : 'Слушать'}</Button>}
      </AccordionDetails>
    </Accordion>)}
    {rules.length > 0 && <Typography sx={{my: 2}}>Просмотрены примеры: {viewed.size} / {rules.length}</Typography>}
    {allViewed && <Alert severity="info" sx={{mb: 2}}>Все примеры просмотрены. Это отметка просмотра, без оценки произношения.</Alert>}
    <Box sx={{display: 'flex', justifyContent: 'center', mt: 2}}><Button variant="contained" disabled={loading || !!error || !allViewed} onClick={() => onNext()}>Продолжить без оценки</Button></Box>
  </CardContent></Card>;
};
export default TajweedStep;
